<template>
  <Box>
    <BoxHead
      icon="fas fa-chart-pie"
      :link="link"
    >
      Portfolio
    </BoxHead>

    <LoaderWithText
      v-if="isFetching"
      size="3_5"
    />

    <div
      v-else
      class="holdings-body"
    >
      <Stack
        v-if="topHoldings.length"
        gap="2"
        class="tw-grow"
      >
        <Stack
          gap="1"
          class="tw-grow"
        >
          <div
            v-for="holding in topHoldings"
            :key="holding.id"
            class="holding-item"
          >
            <p class="holding-symbol">
              {{ holding.ticker.symbol }}
            </p>
            <p class="holding-ticker">
              {{ holding.ticker.full_name }}
            </p>
            <p class="meeting-dollars">
              {{ formatCurrency(holding.dollars) }}
            </p>
          </div>
        </Stack>

        <div
          v-if="viewMore"
          style="margin-top: auto;"
        >
          <Button
            icon-before="fas fa-plus"
            :to="toPath"
            size="3_5"
            spacing="tight"
          >
            {{ viewMore }}
          </Button>
        </div>
      </Stack>

      <EmptyBox
        v-else
        size="3_5"
      >
        <Stack gap="2">
          <p>No holdings found</p>
          <Button
            :to="toPath"
            icon-before="fas fa-plus"
            spacing="tight"
          >
            Add holdings
          </Button>
        </Stack>
      </EmptyBox>
    </div>
  </Box>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Box, BoxHead, Button, EmptyBox, LoaderWithText, Stack } from '@stakeui';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { formatCurrency } from '@frontend/shared/Util.js';

const advisorStore = useAdvisorStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
});

const isFetching = ref(false);
const hasFetched = ref(false);
const holdings = ref([]);

const fetchData = async () => {
  isFetching.value = true;

  try {
    const response = await fetch(`/api/v1/entities/${props.entityExternalId}/holdings/`, {
      method: 'GET',
      headers: advisorStore.headers,
    });


    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }

    const data = await response.json();
    holdings.value = data.portfolio.holdings || [];
  } catch (error) {
    console.error(`Error fetching`, error);
  } finally {
    isFetching.value = false;
    hasFetched.value = true;
  }
};

const toPath = { name: 'entity-portfolio' };
const link = computed(() => {
  return {
    to: toPath,
    label: 'View all',
  };
});

const topHoldings = computed(() => holdings.value.slice(0, 10));
const viewMore = computed(() => holdings.value.length > 10 ? `View ${holdings.value.length - 10} more` : '');

onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.holdings-body {
  display: flex;
  flex-grow: 1;
  overflow: hidden;
}

.holding-item {
  display: flex;
  align-items: center;
  gap: 0.5em;
  justify-content: start;
  font-size: 0.875em;
}

.holding-symbol {
  color: var(--stk-text-strong);
  flex-shrink: 0;
  width: 5em;
  font-weight: 500;
}

.holding-ticker {
  color: var(--stk-text-weak);
  flex-grow: 1;
  font-size: 0.875em;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.holding-dollars {
  color: var(--stk-text-weak);
  text-align: right;
}
</style>
